import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import InputBox from '../../component/InputBox';
import ModalRef, { ModalHeader } from '../../component/ModalRef';
import SelectionBox from '../../component/SelectionBox';
import TextAreaBox from '../../component/TextAreaBox';
import TimeBox from '../../component/TimeBox';
import { CODE_GROUP_ID } from '../../constant/Constant';
import { actionAdd, actionEdit, actionInit, actionSearch } from '../../module/modal/SubjectDetailModule';
import CodeUtil from '../../util/CodeUtil';
import { modalType } from '../../util/ModalUtil';
import StatusTextIconBox from '../../component/StatusTextIconBox';
import SingleFileAttachBox from '../../component/SingleFileAttachBox';

import CancelButtonBox from '../../component/buttons/CancelButtonBox';
import ConfirmButtonBox from '../../component/buttons/ConfirmButtonBox';
import SessionStorageUtil from '../../util/SessionStorageUtil';
import AuthenticationUtil from '../../util/AuthenticationUtil';
import SubjectCodeSearchModal from './SubjectCodeSearchModal';
import SearchInputBox from '../../component/SearchInputBox';
import PopupRef from '../../component/PopupRef';

/* 모달을 닫기 위해서 modalRef를 반드시 전달받아야 함 */
const SubjectDetailModal = ({ type, searchParams, callback, modalRef }) => {
    const dispatch = useDispatch();
    const [semesterId, setSemesterId] = useState(SessionStorageUtil.getCurrentSemesterId());
    const [lessonUniversityCd, setLessonUniversityCd] = useState(AuthenticationUtil.getUserUniversityCd());
    const [subjectTypeCd, setSubjectTypeCd] = useState(CodeUtil.getFirstCode(CODE_GROUP_ID.SUBJECT_TYPE));
    const [subjectId, setSubjectId] = useState('');
    const [subjectNm, setSubjectNm] = useState('');
    const [universitySubjectId, setUniversitySubjectId] = useState('');
    const [universitySubjectNm, setUniversitySubjectNm] = useState('');
    const [creditTypeCd, setCreditTypeCd] = useState(CodeUtil.getFirstCode(CODE_GROUP_ID.CREDIT_TYPE));
    const [creditCnt, setCreditCnt] = useState('');
    const [lessonYear, setLessonYear] = useState('');
    const [lessonDesc, setLessonDesc] = useState('');
    const [classNo, setClassNo] = useState('');
    const [profNm, setProfNm] = useState('');
    const [lessonTypeCd, setLessonTypeCd] = useState(CodeUtil.getFirstCode(CODE_GROUP_ID.LESSON_TYPE));
    const [courseTimes, setCourseTimes] = useState([]);
    const [enrolmentRegStatusCd, setEnrolmentRegStatusCd] = useState('');
    const [lessonPlanFileUid, setLessonPlanFileUid] = useState('');
    const [lessonPlanFileNm, setLessonPlanFileNm] = useState('');

    const { subject } = useSelector((state) => state.subjectDetailState);

    const popupRef = useRef(null);
    const subhModalRef = useRef(null);

    useEffect(() => {
        if (type != modalType.ADD) {
            dispatch(
                actionSearch({
                    params: searchParams,
                }),
            );
        }
        return () => {
            dispatch(actionInit());
        };
    }, []);

    useEffect(() => {
        subject && setSemesterId(subject.semesterId);
        subject && setLessonUniversityCd(subject.lessonUniversityCd);
        subject && setSubjectTypeCd(subject.subjectTypeCd);
        subject && setSubjectId(subject.subjectId);
        subject && setSubjectNm(subject.subjectNm);
        subject && setUniversitySubjectId(subject.universitySubjectId);
        subject && setUniversitySubjectNm(subject.universitySubjectNm);
        subject && setCreditTypeCd(subject.creditTypeCd);
        subject && setCreditCnt(subject.creditCnt);
        subject && setLessonYear(subject.lessonYear);
        subject && setLessonDesc(subject.lessonDesc);
        subject && setClassNo(subject.classNo);
        subject && setProfNm(subject.profNm);
        subject && setLessonTypeCd(subject.lessonTypeCd);
        subject && setCourseTimes(subject.courseTimes);
        subject && setEnrolmentRegStatusCd(subject.enrolmentRegStatusCd);
        subject && setLessonPlanFileUid(subject.lessonPlanFileUid);
        subject && setLessonPlanFileNm(subject.lessonPlanFileNm);
    }, [subject]);

    const onClickSubjectNm = (e) => {
        subhModalRef.current.open(
            <SubjectCodeSearchModal
                callback={(subjectId, subjectNm, subjectTypeCd) => {
                    setSubjectId(subjectId);
                    setSubjectNm(subjectNm);
                    setSubjectTypeCd(subjectTypeCd);
                }}
                modalRef={subhModalRef}
            />,
            'large',
        );
    };

    const onClickCancel = () => {
        modalRef.current.close();
    };

    const onClickConfirm = () => {
        if (type == modalType.DETAIL) {
            modalRef.current.close();
        } else {
            if (subjectNm == undefined || subjectNm == null || subjectNm == '') {
                popupRef.current.openAlert('교과목을 선택/입력해주세요.');
            } else if (classNo == undefined || classNo == null || classNo == '') {
                popupRef.current.openAlert('분반정보를 입력해주세요.');
            } else if (universitySubjectId == undefined || universitySubjectId == null || universitySubjectId == '') {
                popupRef.current.openAlert('개설대학 교과목코드를 입력해주세요.');
            } else if (lessonTypeCd == undefined || lessonTypeCd == null || lessonTypeCd == '') {
                popupRef.current.openAlert('수업방식을 선택해주세요.');
            } else {
                if (type == modalType.ADD) {
                    dispatch(
                        actionAdd({
                            data: {
                                semesterId,
                                lessonUniversityCd,
                                subjectId,
                                classNo,
                                universitySubjectId,
                                courseInfo: {
                                    semesterId,
                                    lessonUniversityCd,
                                    subjectTypeCd,
                                    subjectId,
                                    subjectNm,
                                    universitySubjectId,
                                    universitySubjectNm,
                                    creditTypeCd,
                                    creditCnt,
                                    lessonYear,
                                    lessonDesc,
                                    classNo,
                                    profNm,
                                    lessonTypeCd,
                                    lessonPlanFileUid,
                                },
                                courseTimes: courseTimes.map((time) => ({
                                    semesterId,
                                    lessonUniversityCd,
                                    subjectId,
                                    classNo,
                                    weekdayCd: time.weekdayCd,
                                    bgnTime: time.bgnTime,
                                    endTime: time.endTime,
                                })),
                            },
                            callback: () => {
                                popupRef.current.openInfo('강의정보가 등록되었습니다.', () => {
                                    return function () {
                                        callback && callback();
                                        modalRef.current.close();
                                    };
                                });
                            },
                        }),
                    );
                } else {
                    dispatch(
                        actionEdit({
                            data: {
                                semesterId,
                                lessonUniversityCd,
                                subjectId,
                                classNo,
                                universitySubjectId,
                                courseInfo: {
                                    semesterId,
                                    lessonUniversityCd,
                                    subjectTypeCd,
                                    subjectId,
                                    subjectNm,
                                    universitySubjectId,
                                    universitySubjectNm,
                                    creditTypeCd,
                                    creditCnt,
                                    lessonYear,
                                    lessonDesc,
                                    classNo,
                                    profNm,
                                    lessonTypeCd,
                                    lessonPlanFileUid,
                                },
                                courseTimes: courseTimes.map((time) => ({
                                    semesterId,
                                    lessonUniversityCd,
                                    subjectId,
                                    classNo,
                                    weekdayCd: time.weekdayCd,
                                    bgnTime: time.bgnTime,
                                    endTime: time.endTime,
                                })),
                            },
                            callback: () => {
                                popupRef.current.openInfo('강의정보가 저장되었습니다.', () => {
                                    return function () {
                                        callback && callback();
                                        modalRef.current.close();
                                    };
                                });
                            },
                        }),
                    );
                }
            }
        }
    };

    const onChangeFile = ({ fileUid, fileNm }) => {
        setLessonPlanFileNm(fileNm);
        setLessonPlanFileUid(fileUid);
    };

    return (
        <>
            <ModalHeader
                title={'강의 ' + (type == modalType.ADD ? '등록' : type == modalType.EDIT ? '수정' : '상세')}
                modalRef={modalRef}
            />
            <div className='modal-body'>
                <div
                    className={
                        'con-inner ' + (type == modalType.DETAIL ? ' SubjectDetailModal' : ' SubjectDetailModalEdit')
                    }
                >
                    <div className='form-box-row'>
                        <SelectionBox
                            size='large'
                            title='학기'
                            disabled={type != modalType.ADD}
                            value={semesterId}
                            options={CodeUtil.getCodes(CODE_GROUP_ID.SEMESTER)}
                            onChange={(e) => setSemesterId(e.target.value)}
                            useAll={false}
                        />
                        <SelectionBox
                            title='개설대학'
                            size='medium'
                            disabled
                            value={lessonUniversityCd}
                            options={CodeUtil.getCodes(CODE_GROUP_ID.MAIN_UNIVERSITY)}
                            onChange={(e) => setLessonUniversityCd(e.target.value)}
                            useAll={false}
                        />

                        <SelectionBox
                            title='마이크로디그리 과정'
                            size='large'
                            disabled
                            value={subjectTypeCd}
                            options={CodeUtil.getCodes(CODE_GROUP_ID.SUBJECT_TYPE)}
                            onChange={(e) => setSubjectTypeCd(e.target.value)}
                            useAll={false}
                        />
                    </div>
                    <div className='form-box-row'>
                        {type != modalType.ADD ? (
                            <InputBox title='교과목' size='large' disabled value={subjectNm} />
                        ) : (
                            <SearchInputBox
                                title='교과목'
                                size='large'
                                value={subjectNm}
                                onClick={(e) => onClickSubjectNm(e)}
                            />
                        )}
                        <InputBox
                            title='개설대학 교과목코드'
                            size='medium'
                            disabled={type != modalType.ADD}
                            value={universitySubjectId}
                            onChange={(e) => setUniversitySubjectId(e.target.value)}
                            maxLength='64'
                        />
                        <InputBox
                            title='분반'
                            size='small'
                            disabled={type != modalType.ADD}
                            value={classNo}
                            onChange={(e) => setClassNo(e.target.value)}
                            maxLength='4'
                        />
                        <InputBox
                            title='교강사'
                            size='medium'
                            disabled={type == modalType.DETAIL}
                            value={profNm}
                            onChange={(e) => setProfNm(e.target.value)}
                            maxLength='128'
                        />
                        <InputBox
                            title='학점'
                            size='small'
                            disabled={type == modalType.DETAIL}
                            value={creditCnt}
                            onChange={(e) => setCreditCnt(e.target.value)}
                            number
                            min={1}
                            max={9}
                        />
                    </div>
                    <div className='form-box-row'>
                        <InputBox
                            title='개설대학 교과목명'
                            disabled={type == modalType.DETAIL}
                            value={universitySubjectNm}
                            onChange={(e) => setUniversitySubjectNm(e.target.value)}
                            maxLength='128'
                            size='large'
                        />

                        <InputBox
                            title='학년'
                            size='medium'
                            disabled={type == modalType.DETAIL}
                            value={lessonYear}
                            onChange={(e) => setLessonYear(e.target.value)}
                            maxLength='10'
                        />

                        <SelectionBox
                            title='학점인정타입'
                            disabled={type == modalType.DETAIL}
                            value={creditTypeCd}
                            options={CodeUtil.getCodes(CODE_GROUP_ID.CREDIT_TYPE)}
                            onChange={(e) => setCreditTypeCd(e.target.value)}
                            useAll={false}
                            size='large'
                        />

                        <SelectionBox
                            title='수업방식'
                            disabled={type == modalType.DETAIL}
                            value={lessonTypeCd}
                            options={CodeUtil.getCodes(CODE_GROUP_ID.LESSON_TYPE)}
                            onChange={(e) => setLessonTypeCd(e.target.value)}
                            useAll={false}
                            size='large'
                        />
                        {enrolmentRegStatusCd && (
                            <StatusTextIconBox
                                title='수강신청 등록상태'
                                size='medium'
                                codeGroup={CODE_GROUP_ID.ENROLMENT_REG_STATUS}
                                statusCd={enrolmentRegStatusCd}
                            />
                        )}
                    </div>
                    <div className='form-box-row'>
                        <TextAreaBox
                            title='강의설명'
                            value={lessonDesc}
                            disabled={type == modalType.DETAIL}
                            placeholder='강의에 대한 간단한 설명을 입력하세요.'
                            onChange={(e) => setLessonDesc(e.target.value)}
                        />
                    </div>
                    <div className='form-box-row'>
                        <TimeBox
                            title='강의시간'
                            disabled={type == modalType.DETAIL}
                            times={courseTimes}
                            onChange={setCourseTimes}
                            edit={type == modalType.ADD || type == modalType.EDIT}
                        />
                    </div>
                    <div className='form-box-row'>
                        <SingleFileAttachBox
                            labelName={'수업계획서'}
                            fileUid={lessonPlanFileUid}
                            fileNm={lessonPlanFileNm}
                            onChangeFile={onChangeFile}
                            download
                            editable={type == modalType.DETAIL ? false : true}
                            fileTypeErrorMsg={'파일형식이 맞지 않습니다. 강의계획서는 PDF파일형식만 등록가능합니다.'}
                        />
                    </div>
                </div>
                <div className='modal-btn-wrap'>
                    {type != modalType.DETAIL ? <CancelButtonBox title='취소' onClick={onClickCancel} /> : <></>}
                    <ConfirmButtonBox title='확인' onClick={onClickConfirm} />
                </div>
            </div>
            <PopupRef ref={popupRef} />
            <ModalRef ref={subhModalRef} />
        </>
    );
};

export default SubjectDetailModal;
